import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { formatValueDate } from "utils/functions";
import { format, differenceInCalendarDays } from "date-fns";
import _ from "lodash";
import { useMemo } from "react";
import colors from "../assets/styles/mixins/colors.scss";
import { DASHBOARD_ROLES } from "access-roles/roles";

const noEndColor = colors.pink;
const futureOrderExecutionColor = colors.lightGreen2;
const onGoingColor = colors.palegreen;
const noMissionColor = colors.lightSlateGray1;

export const STATS_GETTERS_MAPPING = {
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_IC]: "dashboardIcStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_ED]: "dashboardEdStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_CL]: "dashboardClStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_MISSIONS]: "dashboardMissionStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_BILLING_STATS]:
    "dashboardBillingStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_FAE]: "dashboardFaeStatsGetter",
  [DASHBOARD_ROLES.ROLE_GET_DASHBOARD_RATIO]: "dashboardRatioStatsGetter",
};
export const calculateEndMissionWidth = (
  noMission,
  ongoingMission,
  futurMission,
  orderEnDateMission,
  allWidths
) => {
  return (
    allWidths.monthContainerWidth -
    (noMission + ongoingMission + futurMission + orderEnDateMission)
  );
};

export const getLastBarIndex = (widths) => {
  for (let j = widths.length - 1; j >= 0; j--) {
    if (widths[j] > 10) {
      return j;
    }
  }
  return -1;
};

const calculateTooltipPositions = (e, widths, allWidths) => {
  const startTooltipPosition = widths[0];
  const rawEndPosition = widths[0] + widths[1] + widths[2];
  const endTooltipPosition =
    rawEndPosition > allWidths?.renderWidth
      ? allWidths?.renderWidth
      : rawEndPosition;
  return { startTooltipPosition, endTooltipPosition };
};

export const assembleMissionData = (e, widths, lastBarIndex, allWidths) => {
  const { startTooltipPosition, endTooltipPosition } =
    calculateTooltipPositions(e, widths, allWidths);
  return {
    id: e.mission_title,
    endDate: e.scheduled_end_date,
    startDate: e.start_date,
    data: {
      "no-mission": widths[0],
      "ongoing-mission": widths[1],
      "futur-mission": widths[2],
      "end-order": widths[3],
      "end-mission": widths[4],
    },
    lastBarIndex: lastBarIndex,
    tooltipPositions: {
      start: startTooltipPosition,
      end: endTooltipPosition,
    },
  };
};

export const renderBar = ({
  width,
  backgroundColor,
  id = null,
  applyDynamicRadiusLeft,
  applyDynamicRadiusRight,
}) => {
  return (
    <span
      id={id}
      className={`bar ${applyDynamicRadiusLeft ? "dynamic-radius-left" : ""} ${applyDynamicRadiusRight ? "dynamic-radius-right" : ""
        }`}
      style={{
        width: `${width}px`,
        backgroundColor,
      }}
    ></span>
  );
};

export const renderAllBars = (e, hasAppliedDynamicRadiusLeft, allWidths) => {
  const barData = [
    { width: e.data["no-mission"], backgroundColor: noMissionColor },
    {
      width: e.data["ongoing-mission"],
      backgroundColor: onGoingColor,
      id: "start_date",
    },
    {
      width: e.data["futur-mission"],
      backgroundColor: futureOrderExecutionColor,
    },
    { width: e.data["end-order"], backgroundColor: noEndColor },
    {
      width: e.data["end-mission"],
      backgroundColor: noMissionColor,
    },
  ];

  return (
    <div
      className="bar-parent"
      style={{
        width: `${allWidths.monthContainerWidth}px`,
      }}
    >
      {barData.map((bar, index) => {
        const applyDynamicRadiusLeft =
          !hasAppliedDynamicRadiusLeft && bar.width > 0;
        if (applyDynamicRadiusLeft) hasAppliedDynamicRadiusLeft = true;
        return renderBar({
          ...bar,
          applyDynamicRadiusLeft,
          applyDynamicRadiusRight: e.lastBarIndex === index,
        });
      })}
    </div>
  );
};

export const useMemoizedWidthByDates = (
  content,
  allWidths,
  lastDate,
  userDate,
  firstDate
) =>
  useMemo(
    () =>
      ({ start, end, additionalCheck = false, contentKey = null }) =>
        content?.map((e) => {
          const scheduledEndBeforeFirstDate =
            firstDate &&
            !_.isNil(e.scheduled_end_date) &&
            new Date(e.scheduled_end_date) < firstDate;

          let realStart = getRealStart(e, start);
          let realEnd = getRealEnd(lastDate, end);

          if (additionalCheck) {
            switch (contentKey) {
              case "start_date":
                if (scheduledEndBeforeFirstDate) {
                  return 0;
                }
                ({ realStart, realEnd } = adjustStartDate(e, start));
                break;
              case "futur-mission":
                if (scheduledEndBeforeFirstDate) {
                  return 0;
                }
                ({ realStart, realEnd } = adjustFuturMission(
                  e,
                  start,
                  lastDate
                ));
                break;
              case "scheduled_end_date":
                ({ realStart, realEnd } = adjustScheduledEndDate(
                  e,
                  lastDate,
                  userDate,
                  firstDate
                ));
                break;
              case "end_date": {
                const adjustedDates = adjustEndDate(e, lastDate);
                if (!adjustedDates) return;
                ({ realStart, realEnd } = adjustedDates);
                break;
              }
              default:
                break;
            }
          }

          if (calcTrueWidth(realEnd, realStart, allWidths) < 10) {
            return 0;
          } else {
            return calcTrueWidth(realEnd, realStart, allWidths);
          }
        }),
    [content, allWidths, lastDate, userDate, firstDate]
  );

export const getRealStart = (e, start) => {
  return new Date(e.start_date) <= start ? start : new Date(e.start_date);
};

export const getRealEnd = (endDate, end) => {
  return endDate >= end ? end : endDate;
};

export const adjustStartDate = (e, start) => {
  return { realStart: start, realEnd: new Date(e.start_date) };
};

export const adjustFuturMission = (e, start, lastDate) => {
  const missionStart = new Date(e.start_date);
  const realStart = missionStart > start ? missionStart : start;
  const endOfLastMonth = new Date(
    lastDate?.getFullYear(),
    lastDate?.getMonth() + 1,
    0
  );
  if (_.isNil(e.scheduled_end_date)) {
    return { realStart, realEnd: endOfLastMonth };
  }
  let realEnd = new Date(e.scheduled_end_date);
  if (realEnd > lastDate) {
    realEnd = endOfLastMonth;
  }
  return { realStart, realEnd };
};

export const adjustScheduledEndDate = (e, lastDate, userDate, firstDate) => {
  if (_.isNil(e.scheduled_end_date)) {
    return { realStart: lastDate, realEnd: lastDate };
  }
  const scheduledEnd = new Date(e.scheduled_end_date);
  const realEnd = new Date(
    lastDate?.getFullYear(),
    lastDate?.getMonth() + 1,
    0
  );
  if (firstDate && scheduledEnd < firstDate) {
    return { realStart: firstDate, realEnd };
  }
  return { realStart: scheduledEnd, realEnd };
};

export const adjustEndDate = (e, lastDate) => {
  if (_.isNil(e.end_date) || new Date(e.end_date) > lastDate) return null;
  return {
    realStart: new Date(e.end_date),
    realEnd: new Date(lastDate?.getFullYear(), lastDate?.getMonth() + 1, 0),
  };
};

export const calcTrueWidth = (realEnd, realStart, allWidths) => {
  const daysDiff = differenceInCalendarDays(realEnd, realStart);
  const width = daysDiff * allWidths.pixelsPerDay;
  const trueWidth = width > 0 && width < 10 ? 0 : width;
  return trueWidth > 0 ? Math.abs(trueWidth) : 0;
};

export const renderDate = ({ date, lang }) => (
  <p className="title-3 violet text-uppercase">
    {formatValueDate({ date, lang })}
  </p>
);

export const daysInMonth = (month, year) => {
  return new Date(year, month, 0).getDate();
};

export const renderTypography = (id, defaultMessage) => (
  <Typography
    className="title-3 violet text-uppercase"
    type="h2"
    label={<FormattedMessage id={id} defaultMessage={defaultMessage} />}
  />
);

export const createHeaderItem = (key, id, value, width) => ({
  label: {
    key,
    value: <FormattedMessage id={id} defaultMessage={value} />,
  },
  width,
  id,
});

export const formatTableContent = ({ data, userScope, setDisplayMission }) => {
  if (!data || !data?.content) return [];
  return data?.content?.map((item) => {
    const { user, mission, client } = item;
    const { profile_id, slug: userSlug, name: userName } = user;
    const {
      slug: missionSlug,
      name: missionName,
      start_date,
      scheduled_end_date,
      end_date,
    } = mission;
    const { slug: clientSlug, name: clientName } = client || {};

    return {
      consultant: (
        <a href={`/${userScope}/${profile_id}/${userSlug}`}>
          {userName || "N/A"}
        </a>
      ),
      mission_title: (
        <a
          href={`/${userScope}/missions/${missionSlug}`}
          onClick={() => setDisplayMission(true)}
        >
          {missionName || "N/A"}
        </a>
      ),
      client: (
        <a href={`/${userScope}/client/${clientSlug}`}>{clientName || "N/A"}</a>
      ),
      start_date: start_date,
      scheduled_end_date: scheduled_end_date,
      end_date: end_date,
    };
  });
};
export const getDates = (userdate) => {
  const datesRange = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date(userdate);
    date.setDate(1);
    date.setMonth(date.getMonth() - i);
    datesRange.push(date);
  }

  for (let i = 1; i <= 6; i++) {
    const date = new Date(userdate);
    date.setMonth(date.getMonth() + i);
    datesRange.push(date);
  }
  return datesRange;
};

export const getFormattedDate = (dates, locale) => {
  if (Array.isArray(dates)) {
    return dates.map((d) => format(d, "MM/yy", { locale }));
  } else if (dates instanceof Date) {
    return [format(dates, "MM/yy", { locale })];
  }
  return [];
};

export const STATS_INFO_TITLE = "stats_popup_title";

export const STATS_DATA = [
  {
    main_title: "stats_popup_mission_ongoing",
    sections: [
      {
        content: ["stats_popup_mission_ongoing_content"],
      },
    ],
  },
  {
    main_title: "stats_settled_FDM_title",
    sections: [
      {
        section_scope: "perso_scope",
        content: [
          "perso_scope_first_line_fdm",
          "perso_scope_second_line_fdm",
          "perso_scope_third_line_fdm",
        ],
      },
      {
        section_scope: "macro_scope",
        content: [
          "macro_scope_first_line_fdm",
          "macro_scope_second_line_fdm",
          "macro_scope_third_line_fdm",
        ],
      },
      {
        section_scope: "admin_scope",
        content: ["admin_scope_first_line_fdm"],
      },
    ],
  },
  {
    main_title: "stats_unsettled_FDM",
    sections: [
      {
        section_scope: "perso_scope",
        content: [
          "perso_scope_first_line_fdm",
          "perso_scope_second_line_fdm",
          "perso_scope_third_line_unsettled",
        ],
      },
      {
        section_scope: "macro_scope",
        content: ["macro_scope_first_line_fdm", "macro_scope_second_line_fdm"],
      },
      {
        section_scope: "admin_scope",
        content: ["admin_scope_first_line_fdm"],
      },
    ],
  },
  {
    main_title: "stats_popup_mission_ongoing",
    main_title_description: "stats_ongoing_missions",
    sections: [
      {
        section_scope: "perso_scope",
        content: [
          "perso_first_line_mission",
          "perso_second_line_mission",
          "perso_third_line_mission",
        ],
      },
      {
        section_scope: "macro_scope",
        content: ["macro_first_line_mission", "macro_second_line_mission"],
      },
      {
        section_scope: "admin_scope",
        content: ["admin_mission"],
      },
    ],
  },
  {
    main_title: "stats_consultant",
    main_title_description: "stats_mode_explanation",
    sections: [
      {
        content: ["stats_mode_perso", "stats_mode_macro", "stats_mode_admin"],
      },
    ],
  },
  {
    main_title: "stats_IC",
    main_title_description: "stats_IC_explanation",
    sections: [
      {
        section_scope: "stats_IC_unsettled",
        content: [
          "stats_IC_unsettled_first_explanation",
          "stats_IC_unsettled_second_explanation",
        ],
      },
      {
        section_scope: "stats_IC_settled",
        content: [
          "stats_IC_settled_first_explanation",
          "stats_IC_settled_second_explanation",
        ],
      },
    ],
  },
  {
    main_title: "stats_ED",
    main_title_description: "stats_ED_explanation",
    sections: [
      {
        section_scope: "stats_ED_no_mission",
        content: ["stats_IC_unsettled_first_explanation"],
      },
      {
        section_scope: "stats_ED_with_mission",
        content: ["stats_ED_with_mission_explanation"],
      },
    ],
  },
  {
    main_title: "stats_CL",
    main_title_description: "stats_CL_explanation",
    sections: [
      {
        section_scope: "stats_CL_unsettled",
        content: ["stats_IC_unsettled_first_explanation"],
      },
      {
        section_scope: "stats_CL_settled",
        content: ["stats_CL_settled_explanation"],
      },
    ],
  },
  {
    main_title: "stats_IC_ratio",
    main_title_description: "stats_IC_ratio_explanation",
    sections: [
      {
        section_scope: "stats_IC_ratio_explanation",
        content: ["stats_IC_ratio_explanation_2"],
      },
      {
        section_scope: "stats_IC_unsettled",
        content: ["stats_IC_ratio_explanation_3"],
      },
    ],
  },
];
